import React, { useState, useEffect, useRef } from 'react';
import { useWebSocketStatus } from '../../../../../shared/lib/comm/gateway_ws/useWebSocketStatus';
import { getAllLogs, LogEntry } from './logsStorageManager';
import { formatBuildPanelTimestamp } from './buildPanelTimestamp';

interface BuildLogsTabProps {
    dockerLogs: string;
    processedLogs: string;
    onClearLogs: () => void;
    onToggleConnection: () => void;
}

const BuildLogsTab: React.FC<BuildLogsTabProps> = ({
    dockerLogs,
    processedLogs,
    onClearLogs,
    onToggleConnection
}) => {
    const wsStatus = useWebSocketStatus();
    const [storedLogs, setStoredLogs] = useState<LogEntry[]>([]);
    const logsEndRef = useRef<HTMLDivElement | null>(null);

    // Load logs from localStorage on mount
    useEffect(() => {
        const loadLogs = () => {
            const logs = getAllLogs();
            setStoredLogs(logs);
        };
        
        loadLogs();
        
        // Listen for log changes
        const handleLogsChanged = () => {
            loadLogs();
        };
        
        window.addEventListener('dockerLogsChanged', handleLogsChanged);
        
        return () => {
            window.removeEventListener('dockerLogsChanged', handleLogsChanged);
        };
    }, []);

    // Auto-scroll to bottom when new logs arrive
    useEffect(() => {
        if (logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [storedLogs]);

    return (
        <div className="flex-1 flex flex-col h-full">
            {/* Header with controls */}
            <div className="flex items-center justify-between p-3 bg-[#0A071B] border-b border-gray-700">
                <div className="flex items-center space-x-3">
                    {/* Status indicators and controls */}
                    <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${wsStatus.isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className={`text-xs ${wsStatus.isConnected ? 'text-green-400' : 'text-red-400'}`}>
                            {wsStatus.isConnected ? 'Connected' : 'Disconnected'}
                        </span>
                        <span className={`text-xs ${
                            storedLogs.length >= 200 
                                ? 'text-yellow-400' 
                                : 'text-gray-400'
                        }`}>
                            {storedLogs.length}/200
                        </span>
                    </div>
                    <button
                        onClick={onClearLogs}
                        className="text-gray-400 hover:text-white p-1.5 transition-colors"
                        title="Clear all logs"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                    <button
                        onClick={onToggleConnection}
                        className={`p-1.5 transition-colors ${
                            wsStatus.isConnected 
                                ? 'text-red-400 hover:text-red-300' 
                                : 'text-green-400 hover:text-green-300'
                        }`}
                        title={wsStatus.isConnected ? 'Disconnect' : 'Connect'}
                    >
                        {wsStatus.isConnected ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {/* Logs display area */}
            <div 
                className="flex-1 overflow-y-auto p-3 bg-[#0A071B] font-mono text-xs"
                style={{ 
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#4B5563 #0A071B',
                    scrollBehavior: 'smooth'
                }}
            >
                {storedLogs.length === 0 ? (
                    <div className="text-center text-gray-500 text-xs py-8">
                        <div className="mb-2 text-gray-400">[INFO] No logs available</div>
                        <div className="text-gray-600">
                            Docker logs will appear here when available
                        </div>
                    </div>
                ) : (
                    <div className="space-y-0">
                        {[...storedLogs]
                            .sort((a, b) => {
                                // Sort by backendTs in ascending order (oldest first)
                                if (!a.backendTs && !b.backendTs) {
                                    return a.timestamp.getTime() - b.timestamp.getTime();
                                }
                                if (!a.backendTs) return 1;
                                if (!b.backendTs) return -1;
                                return a.backendTs.localeCompare(b.backendTs);
                            })
                            .map((log) => (
                            <div key={log.id} className="py-0.5 px-1 hover:bg-white/5 transition-colors">
                                <div className="text-xs leading-tight">
                                    <span className="text-gray-500">
                                        {formatBuildPanelTimestamp(log.timestamp)}
                                    </span>
                                    <span className="mx-1">:</span>
                                    <span className="text-gray-300">
                                        {log.content}
                                    </span>
                                </div>
                            </div>
                        ))}
                        <div ref={logsEndRef} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default BuildLogsTab;
